/**
 * «Детали номера» (canon design-guest v7 §3) — карточка типа номера гостя:
 * галерея фото (swipe/dots), мета-линия (площадь/вместимость/тип кровати),
 * описание, удобства-чипы (i18n), вместимость. Открывается из «Моё
 * проживание» по номеру текущего проживания.
 *
 * Данные: useRoomDetail (GET /v1/rooms/{id} → type_id, затем публичный
 * каталог GET /v1/public/{slug}/room-types по type_id → фото/удобства/имя).
 * Поля, которых нет на бэке (точная площадь/тип кровати в свободной форме),
 * НЕ выдумываем — показываем вместимость и удобства как есть.
 */
import { useState } from 'react';
import { Button, Skeleton } from '@hsc/ui';
import type { PublicRoomType } from '@hsc/types';
import { amenityLabels, formatMoney, photoUrl } from '../../lib/fleet-format.js';
import { useRoomDetail } from './useGuestStay.js';

function capacityLine(type: PublicRoomType | undefined): string {
  if (!type) return '';
  const { capacity_min: lo, capacity_max: hi } = type;
  if (hi <= 0) return '';
  const n = lo > 0 && lo !== hi ? `${lo}–${hi}` : `${hi}`;
  return `до ${n} гост.`;
}

export interface RoomDetailDrawerProps {
  roomId: string;
  /** № номера из контекста проживания (для заголовка-фоллбэка). */
  roomNumber?: string | undefined;
  onClose: () => void;
}

export function RoomDetailDrawer({
  roomId,
  roomNumber,
  onClose,
}: RoomDetailDrawerProps): JSX.Element {
  const { data, isLoading, isError } = useRoomDetail(roomId);
  const [photoIdx, setPhotoIdx] = useState(0);

  const type = data?.type;
  const photos = (type?.photos ?? [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((p) => photoUrl(p.object_key))
    .filter((u): u is string => Boolean(u));
  const amenities = amenityLabels(type?.amenities ?? []);
  const title =
    type?.name && type.name.trim() !== ''
      ? type.name
      : roomNumber
        ? `Номер № ${roomNumber}`
        : 'Ваш номер';

  return (
    <section
      className="fleet-drawer room-detail"
      aria-label="Детали номера"
      data-testid="room-detail"
    >
      <div className="fleet-drawer__head">
        <h1 className="hsc-page__title">Детали номера</h1>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="room-detail-close">
          Назад
        </Button>
      </div>

      {isLoading ? (
        <>
          <Skeleton height={180} radius={14} />
          <Skeleton height={20} radius={8} />
          <Skeleton height={20} radius={8} />
        </>
      ) : isError || !data ? (
        <p role="alert">Не удалось загрузить детали номера.</p>
      ) : (
        <>
          {/* Галерея фото (Aman-style swipe + dots) */}
          {photos.length > 0 ? (
            <figure className="room-detail__gallery" data-testid="room-detail-gallery">
              <img
                src={photos[photoIdx]}
                alt={title}
                className="room-detail__photo"
                loading="lazy"
              />
              {photos.length > 1 && (
                <>
                  <span className="room-detail__count">
                    {photoIdx + 1} / {photos.length}
                  </span>
                  <div className="room-detail__dots" role="tablist" aria-label="Фотографии">
                    {photos.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        role="tab"
                        aria-selected={i === photoIdx}
                        aria-label={`Фото ${i + 1}`}
                        className={`room-detail__dot${i === photoIdx ? ' is-active' : ''}`}
                        onClick={() => setPhotoIdx(i)}
                        data-testid={`room-photo-dot-${i}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </figure>
          ) : (
            <div className="room-detail__nophoto" data-testid="room-detail-nophoto">
              Фото скоро появятся.
            </div>
          )}

          <h3 className="room-detail__title" data-testid="room-detail-title">
            {title}
          </h3>
          <p className="room-detail__meta">
            <span>№ {data.room.number}</span>
            {capacityLine(type) !== '' && <span>◇ {capacityLine(type)}</span>}
            {(type?.amenities ?? []).includes('balcony') && <span>◇ балкон</span>}
          </p>

          {type?.description && type.description.trim() !== '' && (
            <p className="room-detail__desc" data-testid="room-detail-desc">
              {type.description}
            </p>
          )}

          {amenities.length > 0 && (
            <section className="room-detail__section">
              <h4 className="room-detail__subhead">В номере</h4>
              <ul className="room-detail__chips" data-testid="room-detail-amenities">
                {amenities.map((a) => (
                  <li key={a} className="room-detail__chip">
                    {a}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {type && type.min_price_kopecks > 0 && (
            <section className="room-detail__section">
              <h4 className="room-detail__subhead">Цена</h4>
              <p className="room-detail__price" data-testid="room-detail-price">
                {formatMoney(type.min_price_kopecks, type.currency)}
                <span className="room-detail__per"> за ночь</span>
              </p>
            </section>
          )}
        </>
      )}
    </section>
  );
}
